import * as React from 'react';

import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slide,
  Switch,
  Typography,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';

import {
  Contrast,
  Preferences,
  TextSize,
  resetPreferences,
  setPreferences,
  usePreferences,
} from '../accessibility/preferences';
import { tokens } from '../theme';

// Built to the Stitch "Accessibility Options Panel" screen (project 1587770953174176333): a
// side panel with grouped reading options.

function keepDialogFocusVisible(active: Element | null, content: HTMLDivElement) {
  const paper = content.closest('.MuiDialog-paper');
  if (
    !(active instanceof HTMLElement) ||
    !active.matches('input, button') ||
    !paper?.contains(active)
  )
    return;
  const target = active.closest('label') ?? active;
  const rect = target.getBoundingClientRect();
  const scroller = paper.scrollHeight > paper.clientHeight ? paper : content;
  const bounds = scroller.getBoundingClientRect();
  const top = Math.max(0, bounds.top);
  const bottom = Math.min(window.innerHeight, bounds.bottom);
  if (rect.top < top || rect.bottom > bottom) {
    target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }
}

const SlideIn = React.forwardRef(function SlideIn(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return (
    <Slide
      direction='left'
      ref={ref}
      {...props}
    />
  );
});

const groupSx = {
  border: `1px solid ${tokens.outlineVariant}`,
  borderRadius: 3,
  bgcolor: tokens.surfaceLowest,
  px: 2.5,
  py: 1.5,
  minWidth: 0,
} as const;

const rowLabelSx = {
  display: 'block',
  fontWeight: 700,
  color: tokens.onSurface,
  fontSize: '0.9375rem',
  lineHeight: 1.45,
} as const;

function Group({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      component='section'
      aria-labelledby={id}
      sx={groupSx}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 1,
          pt: 0.5,
        }}
      >
        <Typography
          id={id}
          component='h3'
          variant='labelCaps'
          sx={{ color: tokens.onSurfaceVariant }}
        >
          {title}
        </Typography>
        <Typography
          component='span'
          variant='labelCaps'
          aria-hidden='true'
          sx={{ color: tokens.onSurfaceVariant }}
        >
          {number}
        </Typography>
      </Box>
      <Box
        sx={{ display: 'grid', '& > * + *': { borderTop: `1px solid ${tokens.outlineVariant}` } }}
      >
        {children}
      </Box>
    </Box>
  );
}

type SwitchKey =
  | 'textSpacing'
  | 'readableFont'
  | 'underlineLinks'
  | 'strongFocus'
  | 'readingGuide'
  | 'reduceMotion'
  | 'remember';

function SwitchRow({
  name,
  label,
  help,
  preferences,
}: {
  name: SwitchKey;
  label: string;
  help: string;
  preferences: Preferences;
}) {
  const id = `a11y-${name}`;
  return (
    <Box
      component='label'
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.5,
        minHeight: 44,
        cursor: 'pointer',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box
          component='span'
          id={`${id}-label`}
          sx={rowLabelSx}
        >
          {label}
        </Box>
        <Typography
          id={`${id}-help`}
          component='span'
          variant='bodySm'
          sx={{ display: 'block', color: tokens.onSurfaceVariant }}
        >
          {help}
        </Typography>
      </Box>
      <Switch
        checked={preferences[name]}
        onChange={(event) => setPreferences({ [name]: event.target.checked })}
        inputProps={{ 'aria-labelledby': `${id}-label`, 'aria-describedby': `${id}-help` }}
        sx={{
          flexShrink: 0,
          // The off track needs 3:1 against the panel; MUI's default grey is lighter.
          '& .MuiSwitch-track': { bgcolor: tokens.outline, opacity: 1 },
          '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFFFFF' },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            bgcolor: tokens.secondary,
            opacity: 1,
          },
        }}
      />
    </Box>
  );
}

type ChoiceOption<T extends string> = {
  value: T;
  label: string;
  detail?: string;
  sample?: React.ReactNode;
};

// A radio group drawn as tiles. Native radios keep arrow-key movement and screen reader
// announcements; the tile shows focus and the chosen state.
function Choice<T extends string>({
  name,
  legend,
  count,
  value,
  options,
  onChange,
  columns,
}: {
  name: string;
  legend: string;
  count: string;
  value: T;
  options: ChoiceOption<T>[];
  onChange: (value: T) => void;
  columns: Record<string, string | Record<string, string>>;
}) {
  return (
    <Box
      component='fieldset'
      sx={{ border: 0, m: 0, p: 0, py: 1.5, minWidth: 0, containerType: 'inline-size' }}
    >
      <Box
        component='legend'
        sx={{
          p: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 1,
        }}
      >
        <Box
          component='span'
          sx={rowLabelSx}
        >
          {legend}
        </Box>
        <Typography
          component='span'
          variant='bodySm'
          aria-hidden='true'
          sx={{ color: tokens.onSurfaceVariant }}
        >
          {count}
        </Typography>
      </Box>
      <Box sx={{ mt: 1.25, display: 'grid', gap: 1, ...columns }}>
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <Box
              component='label'
              key={option.value}
              className='a11y-choice'
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.25,
                minHeight: 56,
                minWidth: 0,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                cursor: 'pointer',
                border: `2px solid ${selected ? tokens.primary : tokens.outline}`,
                bgcolor: selected ? tokens.surfaceLowest : tokens.surfaceLow,
                color: tokens.onSurface,
                '&:has(input:focus-visible)': {
                  outline: `3px solid ${tokens.accentText}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <input
                type='radio'
                name={name}
                value={option.value}
                checked={selected}
                onChange={() => onChange(option.value)}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  margin: 0,
                  opacity: 0,
                  cursor: 'pointer',
                }}
              />
              {option.sample}
              <Box
                component='span'
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  lineHeight: 1.4,
                  overflowWrap: 'break-word',
                }}
              >
                {option.label}
              </Box>
              {option.detail && (
                <Box
                  component='span'
                  sx={{
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    color: tokens.onSurfaceVariant,
                    overflowWrap: 'break-word',
                  }}
                >
                  {option.detail}
                </Box>
              )}
              {selected && (
                <Box
                  component='span'
                  aria-hidden='true'
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: tokens.primary,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

const textSizeOptions: { value: TextSize; label: string; sample: string }[] = [
  { value: 'default', label: 'Default', sample: '0.875rem' },
  { value: 'large', label: 'Large', sample: '1rem' },
  { value: 'larger', label: 'Larger', sample: '1.125rem' },
  { value: 'largest', label: 'Largest', sample: '1.3125rem' },
];

const contrastOptions: { value: Contrast; label: string; detail: string; swatch: string[] }[] = [
  {
    value: 'default',
    label: 'Default',
    detail: 'Standard colours',
    swatch: ['#F2F3FF', '#0B2545'],
  },
  {
    value: 'high',
    label: 'High contrast',
    detail: 'Darker text, stronger borders',
    swatch: ['#FFFFFF', '#000000'],
  },
  {
    value: 'dark',
    label: 'Dark',
    detail: 'Light text on deep navy',
    swatch: ['#0B1220', '#E7EBF5'],
  },
];

function Swatch({ colors }: { colors: string[] }) {
  return (
    <Box
      component='span'
      aria-hidden='true'
      sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}
    >
      {colors.map((color) => (
        <Box
          component='span'
          key={color}
          sx={{
            width: 14,
            height: 14,
            borderRadius: 0.5,
            bgcolor: color,
            border: `1px solid ${tokens.onSurfaceVariant}`,
          }}
        />
      ))}
    </Box>
  );
}

export default function AccessibilityOptions({ inverted = false }: { inverted?: boolean }) {
  const [optionsOpen, setOptionsOpen] = React.useState(false);
  const [content, setContent] = React.useState<HTMLDivElement | null>(null);
  const preferences = usePreferences();

  React.useEffect(() => {
    if (!optionsOpen || !content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      keepDialogFocusVisible(document.activeElement, content);
    });
    content.querySelectorAll('label').forEach((label) => observer.observe(label));
    observer.observe(content);
    const paper = content.closest('.MuiDialog-paper');
    if (paper) observer.observe(paper);
    return () => observer.disconnect();
  }, [optionsOpen, content]);

  const close = () => setOptionsOpen(false);

  return (
    <>
      <Button
        variant='outlined'
        onClick={() => setOptionsOpen(true)}
        startIcon={<AccessibilityNewIcon aria-hidden='true' />}
        sx={{
          mt: 2,
          ...(inverted ? { color: '#FFFFFF', borderColor: '#CBD5E1' } : {}),
        }}
      >
        Accessibility options
      </Button>
      <Dialog
        open={optionsOpen}
        onClose={close}
        aria-labelledby='accessibility-options-title'
        aria-describedby='accessibility-options-intro'
        TransitionComponent={SlideIn}
        className='a11y-panel'
        sx={{
          '& .MuiDialog-container': { justifyContent: 'flex-end', alignItems: 'stretch' },
          '& .MuiDialog-paper': {
            m: 0,
            width: { xs: '100%', sm: 440 },
            maxWidth: '100%',
            height: '100%',
            maxHeight: '100%',
            borderRadius: { xs: 0, sm: '16px 0 0 16px' },
            bgcolor: tokens.surface,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        onFocus={(event) => {
          if (content) keepDialogFocusVisible(event.target, content);
        }}
      >
        <Box
          className='a11y-panel-header'
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            px: 3,
            py: 2.5,
            bgcolor: tokens.surfaceLowest,
            borderBottom: `1px solid ${tokens.outlineVariant}`,
          }}
        >
          <Box
            aria-hidden='true'
            sx={{
              flexShrink: 0,
              display: 'grid',
              placeItems: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: tokens.primaryFixed,
              color: tokens.primary,
            }}
          >
            <AccessibilityNewIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id='accessibility-options-title'
              component='h2'
              variant='h4'
              sx={{ overflowWrap: 'anywhere' }}
            >
              Accessibility options
            </Typography>
            <Typography
              id='accessibility-options-intro'
              variant='body2'
              sx={{ mt: 0.5 }}
            >
              Adjust how this site looks and moves. Your browser and device settings still apply.
            </Typography>
          </Box>
          <IconButton
            aria-label='Close'
            onClick={close}
            sx={{ flexShrink: 0, mt: -0.5, mr: -1, color: tokens.onSurface }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          ref={setContent}
          sx={{ px: 3, py: 2.5, display: 'grid', gap: 2, alignContent: 'start' }}
        >
          <Group
            id='a11y-group-text'
            number='01'
            title='Text'
          >
            <Choice
              name='a11y-text-size'
              legend='Text size'
              count='4 levels'
              value={preferences.textSize}
              onChange={(textSize) => setPreferences({ textSize })}
              // Four across when the labels fit; two by two on narrow panels or large text.
              columns={{
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                '@container (min-width: 21rem)': {
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                },
              }}
              options={textSizeOptions.map((option) => ({
                value: option.value,
                label: option.label,
                sample: (
                  <Box
                    component='span'
                    aria-hidden='true'
                    sx={{ fontWeight: 700, fontSize: option.sample, lineHeight: 1.2 }}
                  >
                    Aa
                  </Box>
                ),
              }))}
            />
            <SwitchRow
              name='textSpacing'
              label='Text spacing'
              help='More space between lines, words and letters.'
              preferences={preferences}
            />
            <SwitchRow
              name='readableFont'
              label='Readable font'
              help='Atkinson Hyperlegible, designed for readers with low vision.'
              preferences={preferences}
            />
          </Group>

          <Group
            id='a11y-group-colour'
            number='02'
            title='Colour and contrast'
          >
            <Choice
              name='a11y-contrast'
              legend='Contrast'
              count='3 themes'
              value={preferences.contrast}
              onChange={(contrast) => setPreferences({ contrast })}
              columns={{
                gridTemplateColumns: 'minmax(0, 1fr)',
                '@container (min-width: 18rem)': {
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                },
              }}
              options={contrastOptions.map((option) => ({
                value: option.value,
                label: option.label,
                detail: option.detail,
                sample: <Swatch colors={option.swatch} />,
              }))}
            />
            <SwitchRow
              name='underlineLinks'
              label='Underline links'
              help='Show an underline under every link.'
              preferences={preferences}
            />
            <SwitchRow
              name='strongFocus'
              label='Stronger focus outline'
              help='A thick, high-visibility ring around the focused button, link or field.'
              preferences={preferences}
            />
          </Group>

          <Group
            id='a11y-group-reading'
            number='03'
            title='Reading and motion'
          >
            <SwitchRow
              name='readingGuide'
              label='Reading guide'
              help='A bar that follows the pointer and keyboard focus.'
              preferences={preferences}
            />
            <SwitchRow
              name='reduceMotion'
              label='Reduce motion'
              help='Turn off animations and smooth scrolling.'
              preferences={preferences}
            />
          </Group>

          <Box
            sx={{
              ...groupSx,
              bgcolor: tokens.surfaceContainer,
              borderColor: tokens.surfaceContainer,
            }}
          >
            <SwitchRow
              name='remember'
              label='Remember on this device'
              help='Saved in this browser only. Nothing is sent to us.'
              preferences={preferences}
            />
          </Box>
        </DialogContent>

        <DialogActions
          className='a11y-panel-actions'
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            bgcolor: tokens.surfaceLowest,
            borderTop: `1px solid ${tokens.outlineVariant}`,
          }}
        >
          <Button
            onClick={resetPreferences}
            sx={{ color: tokens.onSurface }}
          >
            Reset all
          </Button>
          <Button
            variant='contained'
            onClick={close}
            sx={{ minWidth: 112 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
